/**
 * Music ducking helper — explicit caller-driven duck/unduck.
 *
 * Web Audio API refs (verified via MDN / webaudio.github.io spec):
 * - `AudioParam.linearRampToValueAtTime(value, endTime)`: schedules a linear
 *   ramp from the previous scheduled value to `value`; `endTime` is an ABSOLUTE
 *   AudioContext time, not a duration offset.
 * - `AudioParam.cancelScheduledValues(cancelTime)`: cancels all events whose
 *   start time is ≥ `cancelTime`; the canonical "smooth re-anchor" pattern
 *   pairs this with an immediate `linearRampToValueAtTime` from the current
 *   live value to avoid clicks.
 */

/** Music ducking parameters per coordinator special instruction §14. */
export const DUCK_PARAMS = {
  defaultDepth: 0.3, // multiplier on user volume (30% during duck)
  rampMs: 200, // time for both duck-in and duck-out ramps
} as const;

export interface MusicDucker {
  duck(durationMs: number, depth?: number): void;
  unduck(): void;
}

export interface MusicDuckerDeps {
  readonly audioContext: AudioContext;
  readonly musicGain: GainNode;
  readonly getUserMusicVolume: () => number;
}

type DuckState = 'idle' | 'ducking' | 'held' | 'reverting';

/**
 * Creates a MusicDucker that schedules Web Audio AudioParam ramps on
 * `musicGain.gain` to lower and restore music volume around SFX events.
 *
 * The ducker is REENTRANT: a second `duck()` while one is in progress cancels
 * the prior schedule via `cancelScheduledValues` and re-anchors fresh ramps,
 * avoiding audio clicks per the canonical Web Audio smooth-reanchor pattern.
 *
 * The ducker NEVER reads or writes localStorage. All state is in-memory and
 * all audio interaction is via AudioParam scheduling.
 */
export function createMusicDucker(deps: MusicDuckerDeps): MusicDucker {
  const { audioContext, musicGain, getUserMusicVolume } = deps;
  const gain = musicGain.gain;
  const rampSec = DUCK_PARAMS.rampMs / 1000;

  let state: DuckState = 'idle';

  function duck(durationMs: number, depth?: number): void {
    const now = audioContext.currentTime;
    const userVolume = getUserMusicVolume();
    const duckedValue = userVolume * (depth ?? DUCK_PARAMS.defaultDepth);
    const durationSec = durationMs / 1000;

    // Reentrant: cancel any in-progress schedule first
    if (state !== 'idle') {
      gain.cancelScheduledValues(now);
    }

    state = 'ducking';

    // Stage 1 — ramp DOWN to ducked level over rampMs
    gain.linearRampToValueAtTime(duckedValue, now + rampSec);

    // Stage 2 — hold: no scheduling needed; param stays at ducked value

    // Stage 3 — ramp BACK UP to userVolume after durationMs + rampMs
    gain.linearRampToValueAtTime(userVolume, now + durationSec + rampSec);
  }

  function unduck(): void {
    // NO-OP when idle — never schedule a ramp from current to current
    if (state === 'idle') {
      return;
    }

    const now = audioContext.currentTime;
    const userVolume = getUserMusicVolume();

    gain.cancelScheduledValues(now);
    gain.linearRampToValueAtTime(userVolume, now + rampSec);

    state = 'idle';
  }

  return { duck, unduck };
}
